import React, { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import { Box, Paper, Typography, Stack, Link } from '@mui/material';
import 'leaflet/dist/leaflet.css';
import { statusColor, statusLabel, INK } from '../../utils/commercialColors';

const LEGEND_STATUSES = ['PROSPECT', 'TRIAL', 'PAID', 'CANCELLATION_SCHEDULED', 'CHURNED'];

// react-leaflet's declarative <Popup> renders React children, not raw
// HTML strings via innerHTML - unlike MainBoard.jsx's imperative Leaflet
// usage (which builds popup DOM nodes by hand specifically to avoid a
// stored-XSS risk from string HTML), this is safe by construction and
// needs no such workaround.
export default function CommercialMap({ facilities }) {
  const points = useMemo(
    () => (facilities || []).filter((f) => Number(f.lat) !== 0 && Number(f.lon) !== 0),
    [facilities],
  );

  // Averaging lat/lon breaks badly here: the pilot zones span mainland
  // France and Guadeloupe (~7000km apart), so a naive centroid lands in
  // the middle of the Atlantic with every real marker off-screen. Fitting
  // bounds instead always keeps every visible marker in frame - honestly
  // zoomed out when "Toutes" spans both clusters, and naturally zoomed in
  // once the dashboard's zone filter narrows facilities to one cluster.
  const bounds = points.length ? points.map((f) => [Number(f.lat), Number(f.lon)]) : null;
  const FRANCE_CENTER = [46.6, 2.2]; // fallback when nothing is geolocated yet

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Carte commerciale
      </Typography>
      <Stack direction="row" spacing={2} sx={{ mb: 1, flexWrap: 'wrap' }}>
        {LEGEND_STATUSES.map((s) => (
          <Stack key={s} direction="row" spacing={0.5} alignItems="center">
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: statusColor(s) }} />
            <Typography variant="caption" sx={{ color: INK.secondary }}>{statusLabel(s)}</Typography>
          </Stack>
        ))}
      </Stack>
      <Box sx={{ height: 400, width: '100%' }}>
        <MapContainer
          {...(bounds
            ? { bounds, boundsOptions: { padding: [40, 40], maxZoom: 12 } }
            : { center: FRANCE_CENTER, zoom: 5 })}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution="&copy; OpenStreetMap contributors"
          />
          {points.map((f) => (
            <CircleMarker
              key={f.facility_id}
              center={[Number(f.lat), Number(f.lon)]}
              radius={8}
              pathOptions={{ color: statusColor(f.status), fillColor: statusColor(f.status), fillOpacity: 0.85 }}
            >
              <Popup>
                <strong>{f.facility_name}</strong>
                <br />
                {statusLabel(f.status)} — {f.zone || 'zone non définie'}
                {f.agent_name && (
                  <>
                    <br />
                    Ambassadeur : {f.agent_name}
                  </>
                )}
                <br />
                <Link component={RouterLink} to={`/admin/commercial/facility/${f.facility_id}`}>
                  Voir la fiche établissement
                </Link>
              </Popup>
            </CircleMarker>
          ))}
        </MapContainer>
      </Box>
      {points.length === 0 && (
        <Typography variant="caption" sx={{ color: INK.muted, display: 'block', mt: 1 }}>
          Aucun établissement géolocalisé pour l'instant.
        </Typography>
      )}
    </Paper>
  );
}
